export type HealthScorePeriodDirection = "up" | "down" | "flat";

export type HealthScoreContextLine = {
  key: string;
  text: string;
  direction?: HealthScorePeriodDirection;
};

export function healthScoreContextAria(
  lines: HealthScoreContextLine[],
): string {
  return lines
    .map((line) => {
      if (line.direction === "up") {
        return `up ${line.text}`;
      }

      if (line.direction === "down") {
        return `down ${line.text}`;
      }

      return line.text;
    })
    .join(". ");
}

export type HealthScoreContextInput = {
  contributorCount?: number | null;
  enrolledMembers?: number | null;
  currentScore?: number | null;
  previousScore?: number | null;
};

export function healthScoreContextLines(
  input: HealthScoreContextInput,
): HealthScoreContextLine[] {
  const lines: HealthScoreContextLine[] = [];
  const contributors = finiteCount(input.contributorCount);
  if (contributors != null && contributors >= 1) {
    lines.push({
      key: "contributors",
      text:
        contributors === 1
          ? "Based on 1 active contributor"
          : `Based on ${contributors} active contributors`,
    });
  }

  const current = finiteNumber(input.currentScore);
  const previous = finiteNumber(input.previousScore);
  if (current != null && previous != null) {
    lines.push(periodDeltaLine(current, previous));
  }

  const coverage = signalCoverageLine(
    contributors,
    finiteCount(input.enrolledMembers),
  );
  if (coverage != null) {
    lines.push(coverage);
  }

  if (current != null && previous == null) {
    lines.push({
      key: "period",
      text: "Keep building momentum to see more data",
    });
  }

  return lines;
}

function signalCoverageLine(
  contributors: number | null,
  enrolled: number | null,
): HealthScoreContextLine | null {
  if (contributors == null || enrolled == null || enrolled < 1) {
    return null;
  }

  const percent = Math.round((contributors / enrolled) * 100);
  return {
    key: "coverage",
    text: `Contributors signal coverage: ${percent}% of enrolled members in this score`,
  };
}

/** First scored day in the window, when a later scored day exists and the span is at least 28 days. */
export function earliestComparisonScore(
  daily: { date?: string; wellness: number | null }[] | null | undefined,
): number | null {
  if (!daily) {
    return null;
  }

  const scored = daily.filter(
    (day) =>
      day.date != null && day.wellness != null && Number.isFinite(day.wellness),
  );
  if (scored.length < 2) {
    return null;
  }

  const span = inclusiveUtcDays(scored[0].date, scored[scored.length - 1].date);
  if (span == null || span < 28) {
    return null;
  }

  return scored[0].wellness ?? null;
}

function inclusiveUtcDays(start?: string, end?: string): number | null {
  if (!start || !end) {
    return null;
  }

  const startMs = Date.parse(`${start.slice(0, 10)}T00:00:00Z`);
  const endMs = Date.parse(`${end.slice(0, 10)}T00:00:00Z`);
  if (Number.isNaN(startMs) || Number.isNaN(endMs)) {
    return null;
  }

  return Math.round((endMs - startMs) / 86_400_000) + 1;
}

function periodDeltaLine(
  current: number,
  previous: number,
): HealthScoreContextLine {
  const delta = Math.round(current) - Math.round(previous);
  if (delta > 0) {
    return {
      key: "period",
      text: `${delta} versus previous 4 weeks`,
      direction: "up",
    };
  }

  if (delta < 0) {
    return {
      key: "period",
      text: `${Math.abs(delta)} versus previous 4 weeks`,
      direction: "down",
    };
  }

  return {
    key: "period",
    text: "No change versus previous 4 weeks",
    direction: "flat",
  };
}

function finiteCount(value: number | null | undefined): number | null {
  if (value == null || !Number.isFinite(value)) {
    return null;
  }

  return Math.trunc(value);
}

function finiteNumber(value: number | null | undefined): number | null {
  if (value == null || !Number.isFinite(value)) {
    return null;
  }

  return value;
}
